import { ClipboardList, CheckCircle2 } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
} from "../../components/ui";
import { Seo } from "../../lib/head";
import { RelatedLinks } from "../../components/marketing";

const RULES = [
  "Anticholinergic burden (Boustani 2008 ACB scale)",
  "STOPP/START v2 (O'Mahony 2015)",
  "Beers 2023 high-priority list (AGS)",
  "Gradual Dose Reduction worksheet — F605 (psychotropic / GDR)",
  "Antibiotic stewardship + F881",
  "Morse fall risk + NRS-11 pain assessment",
];

export function SnfSolution() {
  return (
    <>
      <Seo title="SNF Medication Regimen Review Software · F757 & F605" description="SNF medication regimen review (MRR) software, ASCP-aligned. F756, F757, F758, F605, and F881 coverage. Beers + STOPP/START + GDR + antibiotic stewardship, out of the box." path="/solutions/snf" />
      <Section className="pb-8">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <Eyebrow>Long-term care</Eyebrow>
              <Heading level={1} className="mt-3">
                ASCP-aligned reviews. F-tag coverage. No spreadsheets.
              </Heading>
              <Lede className="mt-6">
                Built for consultant pharmacists supporting skilled nursing
                facilities under ASCP standards and CMS conditions of
                participation.
              </Lede>
              <div className="mt-8 flex gap-3">
                <LinkButton to="/demo">Book an SNF demo</LinkButton>
                <LinkButton to="/features" variant="secondary">
                  See all features
                </LinkButton>
              </div>
            </div>
            <Card className="bg-forest-gradient text-white border-transparent">
              <ClipboardList className="h-10 w-10 mb-4" aria-hidden />
              <p className="text-sm uppercase tracking-tagline font-semibold text-white/85">
                ASCP-aligned
              </p>
              <p className="mt-3 text-2xl font-bold">
                SNF rule pack across 6 clinical domains
              </p>
              <p className="mt-4 text-white/85 text-sm">
                Each rule is anchored to a citation. Severity, NA reasons, and
                allergy suppression resistance are built in.
              </p>
            </Card>
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>Rule coverage</Eyebrow>
            <Heading level={2} className="mt-2">
              Coverage your medical director will recognize.
            </Heading>
          </div>
          <ul className="mt-10 grid gap-4 md:grid-cols-2">
            {RULES.map((r) => (
              <li
                key={r}
                className="flex items-start gap-3 rounded-xl border border-border bg-surface p-4"
              >
                <CheckCircle2 className="h-5 w-5 text-forest-700 mt-0.5" aria-hidden />
                <span className="text-sm text-ink-800 font-medium">{r}</span>
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <Heading level={3}>F-tag coverage</Heading>
              <p className="mt-3 text-ink-600">
                F605 psychotropic / GDR · F757 unnecessary drug use · F881
                antibiotic stewardship.
              </p>
            </div>
            <div>
              <Heading level={3}>Assessment worksheets</Heading>
              <p className="mt-3 text-ink-600">
                ACB (Boustani 2008), STOPP/START v2, Beers 2023, GDR, abx
                stewardship, Morse fall risk, NRS-11 pain.
              </p>
            </div>
            <div>
              <Heading level={3}>Workflow</Heading>
              <p className="mt-3 text-ink-600">
                Chart in → flags out → bundle render → sign-off → invoice.
                Auditable end-to-end.
              </p>
            </div>
          </div>
        </Container>
      </Section>
      <RelatedLinks
        items={[
          { to: "/medication-regimen-review-software", label: "MRR software", desc: "The monthly review, chart ingest to invoice." },
          { to: "/survey-readiness", label: "Survey readiness", desc: "Automated F-tag & GDR documentation." },
          { to: "/resources/f-tag-readiness-checklist", label: "F-tag checklist", desc: "Prep before your next SNF survey." },
        ]}
      />
    </>
  );
}
